import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface ApiResponse<T> {
    statusCode: number;
    data: T;
    message?: string;
}


// Types
export interface ControlData {
    id?: string;
    standard?: string;
    section?: string;
    topic?: string;
    controlID?: string;
    controlDescription?: string;
    controlName?: string;
    controlOwner?: string;
    controlApplicable?: boolean;
    status?: string;
    category?: string;
    updatedAt?: any;
    assets?: string[];
    tasks?: string[];
    supportingDocuments?: string[];
    policies?: string[];
    relatedConfigurationStandard?: string[];
    risks?: string[];
    relatedControls?: string[];
    ismsEntries?: string[];
    ismsObjectives?: string[];
    legalObligations?: string[];
    [key: string]: any; // For additional dynamic properties
}

export interface Control extends ControlData { }

export interface ConfigurationStandards {
    [key: string]: {
        controls: ControlData[];
    };
}

export interface ControlsResponse {
    ConfigurationStandards: ConfigurationStandards;
    Controls: ControlData[];
}

// Component link interface for linked items in controls
export interface ComponentLink {
    id: string;              // canonical identifier in the format <scheme>[:<version>]:<ref>
    domain: string;          // classification (Asset, Policy, Risk, Control, etc.)
    scheme: string;          // namespace (internal, iso27001:2013, nist800-53r5, etc.)
    ref: string;             // raw identifier inside the scheme
    title?: string;          // human-readable label (optional)
    relation: string;        // relationship (implements, maps-to, mitigates, etc.)
    confidence: number;      // number between 0-1 for mapping certainty
}

export interface BulkOperation {
    controlIds: string[];
    type: 'assets' | 'documents' | 'policies' | 'risks' | 'controls' | 'legalObligations' | 'linkedComponents';
    action: 'add' | 'remove' | 'update';
    data: any;
    selectedControls?: string[];
    standard?: string;
}

// Debounce cache for rate limit protection
const pendingRequests = new Map<string, Promise<any>>();

// Simple API wrapper with debouncing (rate limiting is now handled globally by deiterateAPI)
async function makeRateLimitAwareRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    options?: any
): Promise<T> {
    // Create a unique key for this request
    const requestKey = `${method}:${url}:${JSON.stringify(options)}`;
    
    // If there's already a pending request for this exact call, return it
    if (pendingRequests.has(requestKey)) {
        console.log(`[MCMService] Debouncing duplicate request: ${requestKey}`);
        return pendingRequests.get(requestKey);
    }
    
    // Add priority to options for global rate limiting
    const optionsWithPriority = {
        ...options,
        priority: 0 // Low priority for MCM operations to not block user edits
    };
    
    // Create the request promise
    const requestPromise = (async () => {
        try {
            let response: any;
            
            switch (method) {
                case 'GET':
                    response = await deiterateAPI.GET<T>(url, optionsWithPriority);
                    break;
                case 'POST':
                    response = await deiterateAPI.POST<T>(url, optionsWithPriority);
                    break;
                case 'PUT':
                    response = await deiterateAPI.PUT<T>(url, optionsWithPriority);
                    break;
                case 'DELETE':
                    response = await deiterateAPI.DELETE<T>(url, optionsWithPriority);
                    break;
                case 'PATCH':
                    response = await deiterateAPI.PATCH<T>(url, optionsWithPriority);
                    break;
            }
            
            return response;
        } catch (error: any) {
            // Rate limiting and retries are now handled globally by deiterateAPI
            throw error;
        }
    })();
    
    // Store the promise
    pendingRequests.set(requestKey, requestPromise);
    
    // Clean up when done
    requestPromise.finally(() => {
        pendingRequests.delete(requestKey);
    });
    
    return requestPromise;
}

class MCMService {
    private static standardNameCache: Record<string, string> = {};
    
    // Controls cache with TTL
    private static controlsCache: {
        data: ControlsResponse | null;
        timestamp: number;
        promise: Promise<ControlsResponse> | null;
    } = { data: null, timestamp: 0, promise: null };
    
    // Cache for controls by standard
    private static controlsByStandardCache: Record<string, {
        data: ControlData[];
        timestamp: number;
        promise: Promise<ControlData[]> | null;
    }> = {};
    
    // Cache TTL in milliseconds (5 minutes)
    private static CACHE_TTL = 5 * 60 * 1000;

    // Clear all caches (call after mutations)
    static clearCache(): void {
        this.controlsCache = { data: null, timestamp: 0, promise: null };
        this.controlsByStandardCache = {};
    }

    // Resolve a standard code (e.g., iso27001) to a display name (e.g., ISO 27001) via API
    static async resolveStandardName(code: string): Promise<string> {
        if (!code) return 'generic';
        const key = String(code).toLowerCase();
        if (this.standardNameCache[key]) return this.standardNameCache[key];

        try {
            const response = await deiterateAPI.GET<ApiResponse<{ code: string; name: string }>>(
                `/v1/mcm/standard-name/${encodeURIComponent(code)}`,
                { backend: 'thalassa' }
            );
            if (response.data?.name) {
                this.standardNameCache[key] = response.data.name;
                return response.data.name;
            }
        } catch (e) {
            // ignore and fallback to code
        }
        this.standardNameCache[key] = code;
        return code;
    }

    // Get all controls from all modules via API (with caching)
    static async getControls(useISM: boolean = false, tenancyID: string): Promise<ControlsResponse> {
        const now = Date.now();
        
        // Return cached data if still valid
        if (this.controlsCache.data && (now - this.controlsCache.timestamp) < this.CACHE_TTL) {
            return this.controlsCache.data;
        }
        
        // If there's already a request in flight, wait for it
        if (this.controlsCache.promise) {
            return this.controlsCache.promise;
        }
        
        // Make the request
        this.controlsCache.promise = (async () => {
            try {
                const response = await deiterateAPI.GET<ApiResponse<ControlsResponse>>(
                    '/v1/mcm/controls',
                    { backend: 'thalassa' }
                );
                
                if (response.statusCode === 200 && response.data) {
                    this.controlsCache.data = response.data;
                    this.controlsCache.timestamp = Date.now();
                    return response.data;
                }
                
                return { ConfigurationStandards: {}, Controls: [] };
            } catch (error) {
                console.error('Error in getControls:', error);
                throw error;
            } finally {
                this.controlsCache.promise = null;
            }
        })();
        
        return this.controlsCache.promise;
    }

    // Get a single control by ID via API
    static async getControlById(controlID: string, tenancyID?: string): Promise<ControlData | null> {
        try {
            if (!tenancyID) {
                console.log('[mcm] No tenancy ID provided');
                return null;
            }

            const response = await makeRateLimitAwareRequest<ApiResponse<ControlData | null>>(
                'GET',
                `/v1/mcm/controls/${encodeURIComponent(controlID)}`,
                { backend: 'thalassa' }
            );
            
            if (response.statusCode === 200) {
                return response.data || null;
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching control:', error);
            throw error;
        }
    }

    // Helper function to clean up undefined values from an object
    private static cleanUndefinedValues(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }
        
        if (Array.isArray(obj)) {
            return obj.map(item => this.cleanUndefinedValues(item));
        }
        
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value === undefined) {
                return acc;
            }
            return {
                ...acc,
                [key]: this.cleanUndefinedValues(value)
            };
        }, {});
    }

    // Create a new control via API
    static async createControl(control: Omit<ControlData, 'controlID'>, tenancyID: string): Promise<ControlData> {
        try {
            const newControl = this.cleanUndefinedValues({
                ...control,
                controlID: control.controlID || `custom-${Date.now()}`,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            const response = await deiterateAPI.POST<ApiResponse<ControlData>>(
                '/v1/mcm/controls',
                { backend: 'thalassa', body: JSON.stringify(newControl) }
            );
            
            // Clear cache after mutation
            this.clearCache();
            
            if (response.statusCode === 200 && response.data) {
                return response.data;
            }
            
            return newControl as ControlData;
        } catch (error) {
            console.error('Error creating control:', error);
            throw error;
        }
    }

    // Update an existing control via API
    static async updateControl(
        controlID: string,
        updates: Partial<ControlData>,
        tenancyID: string
    ): Promise<void> {
        try {
            await deiterateAPI.PUT<ApiResponse<void>>(
                `/v1/mcm/controls/${encodeURIComponent(controlID)}`,
                { backend: 'thalassa', body: JSON.stringify(updates) }
            );
            // Clear cache after mutation
            this.clearCache();
        } catch (error) {
            console.error('Error updating control:', error);
            throw error;
        }
    }

    // Mark a control as deleted via API
    static async deleteControl(controlID: string, tenancyID: string): Promise<void> {
        try {
            await deiterateAPI.DELETE<ApiResponse<void>>(
                `/v1/mcm/controls/${encodeURIComponent(controlID)}`,
                { backend: 'thalassa' }
            );
            // Clear cache after mutation
            this.clearCache();
        } catch (error) {
            console.error('Error deleting control:', error);
            throw error;
        }
    }

    // Bulk update controls via API
    static async bulkUpdate(operation: BulkOperation, tenancyID: string): Promise<void> {
        try {
            await makeRateLimitAwareRequest<ApiResponse<void>>(
                'POST',
                '/v1/mcm/controls/bulk',
                { backend: 'thalassa', body: JSON.stringify(operation) }
            );
            // Clear cache after mutation
            this.clearCache();
        } catch (error) {
            console.error('Error in bulk update:', error);
            throw error;
        }
    }

    // Search controls via API
    static async searchControls(qStr: string): Promise<ControlData[]> {
        try {
            const response = await deiterateAPI.GET<ApiResponse<ControlData[]>>(
                `/v1/mcm/controls/search?q=${encodeURIComponent(qStr)}`,
                { backend: 'thalassa' }
            );
            
            if (response.statusCode === 200 && response.data) {
                return response.data;
            }
            
            return [];
        } catch (error) {
            console.error('Error searching controls:', error);
            throw error;
        }
    }

    // Get controls by standard via API (with caching)
    static async getControlsByStandard(standard: string): Promise<ControlData[]> {
        const now = Date.now();
        const cacheKey = standard.toLowerCase();
        const cached = this.controlsByStandardCache[cacheKey];
        
        // Return cached data if still valid
        if (cached?.data && (now - cached.timestamp) < this.CACHE_TTL) {
            return cached.data;
        }
        
        // If there's already a request in flight, wait for it
        if (cached?.promise) {
            return cached.promise;
        }
        
        // Initialize cache entry if needed
        if (!this.controlsByStandardCache[cacheKey]) {
            this.controlsByStandardCache[cacheKey] = { data: [], timestamp: 0, promise: null };
        }
        
        // Make the request
        this.controlsByStandardCache[cacheKey].promise = (async () => {
            try {
                const response = await deiterateAPI.GET<ApiResponse<ControlData[]>>(
                    `/v1/mcm/controls/by-standard/${encodeURIComponent(standard)}`,
                    { backend: 'thalassa' }
                );
                
                if (response.statusCode === 200 && response.data) {
                    this.controlsByStandardCache[cacheKey].data = response.data;
                    this.controlsByStandardCache[cacheKey].timestamp = Date.now();
                    return response.data;
                }
                
                return [];
            } catch (error) {
                console.error(`Error fetching controls for standard ${standard}:`, error);
                throw error;
            } finally {
                this.controlsByStandardCache[cacheKey].promise = null;
            }
        })();
        
        return this.controlsByStandardCache[cacheKey].promise!;
    }

    // Get controls with pagination (uses getControls API and paginates client-side)
    static async getControlsPaginated(
        page: number = 1,
        limit: number = 50,
        standard?: string
    ): Promise<{ controls: ControlData[]; total: number }> {
        try {
            // Get all controls via API
            let allControls: ControlData[] = [];
            
            if (standard) {
                allControls = await this.getControlsByStandard(standard);
            } else {
                const response = await this.getControls(false, '');
                // Flatten the controls from the response
                allControls = response.Controls;
            }

            const start = (page - 1) * limit;
            const end = start + limit;
            const paginatedControls = allControls.slice(start, end);

            return {
                controls: paginatedControls,
                total: allControls.length
            };
        } catch (error) {
            console.error('Error fetching paginated controls:', error);
            throw error;
        }
    }
    
    // Detect domain from ID patterns - used for intelligent domain assignment
    static detectDomainFromId(id: string): string | null {
        if (!id) return null;
        
        // Common prefix patterns
        if (/^AST-\d+/i.test(id)) return 'Asset';
        if (/^ASS-\d+/i.test(id)) return 'Assurance';
        // Match any occurrence of 'ass' (case-insensitive) followed by digits, e.g., 'disp_ass001', 'ass001', 'ismass001'
        if (/ass\d+/i.test(id)) return 'Assurance';
        if (/^POL-\d+/i.test(id) || /^policy_\d+/i.test(id)) return 'Policy';
        if (/^RSK-\d+/i.test(id)) return 'Risk';
        if (/^DOC-\d+/i.test(id)) return 'Document';
        if (/^INC-\d+/i.test(id)) return 'Incident';
        if (/^A\.\d+\.\d+/i.test(id) || /^ISO\d+/i.test(id)) return 'Control';
        
        // Try to match common patterns in IDs
        if (id.toLowerCase().includes('asset')) return 'Asset';
        if (id.toLowerCase().includes('policy')) return 'Policy';
        if (id.toLowerCase().includes('risk')) return 'Risk';
        if (id.toLowerCase().includes('doc')) return 'Document';
        if (id.toLowerCase().includes('control')) return 'Control';
        if (id.toLowerCase().includes('incident')) return 'Incident';
        
        // Return null if no match
        return null;
    }
    
    // Get relation type based on domain
    static getRelationForDomain(domain: string): string {
        switch (domain) {
            case 'Asset': return 'impacted-by';
            case 'Document': return 'supports';
            case 'Policy': return 'implements';
            case 'Risk': return 'mitigates';
            case 'Control': return 'maps-to';
            case 'Standard': return 'complies-with';
            case 'Procedure': return 'implements';
            case 'Incident': return 'related-to';
            default: return 'maps-to';
        }
    }

    static normalizeDomainKey(domain: string): string {
        const s = String(domain || '').trim();
        if (!s) return 'Other';
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    // Get component by ID via API
    static async getComponentById(domain: string, componentID: string, tenancyID: string): Promise<any> {
        try {
            const response = await makeRateLimitAwareRequest<ApiResponse<any>>(
                'GET',
                `/v1/mcm/components/${encodeURIComponent(domain)}/${encodeURIComponent(componentID)}`,
                { backend: 'thalassa' }
            );
            
            if (response.statusCode === 200) {
                return response.data || null;
            }
            return null;
        } catch (e) {
            console.warn('[MCMService.getComponentById] failed', e);
            return null;
        }
    }

    // Update component links via API
    static async updateComponentLinks(tenancyID: string, domain: string, componentID: string, links: ComponentLink[], meta?: { title?: string }): Promise<void> {
        try {
            await makeRateLimitAwareRequest<ApiResponse<void>>(
                'PUT',
                `/v1/mcm/components/${encodeURIComponent(domain)}/${encodeURIComponent(componentID)}/links`,
                { backend: 'thalassa', body: JSON.stringify({ links, title: meta?.title }) }
            );
        } catch (e) {
            console.warn('[MCMService.updateComponentLinks] failed', e);
            throw e;
        }
    }
}

export default MCMService;
export {
    MCMService,
    MCMService as mcmService
}